import React, { useState, useEffect } from 'react';
import { GridFourIcon, SparkleIcon } from '@phosphor-icons/react';

interface LookbookToolbarProps {
    isGeneratorOpen: boolean;
    onGeneratorToggle: () => void;
    isGalleryOpen: boolean;
    onGalleryToggle: () => void;
    showGenerator?: boolean; // Hide generator button for viewers
}

export const LookbookToolbar: React.FC<LookbookToolbarProps> = ({
    isGeneratorOpen,
    onGeneratorToggle,
    isGalleryOpen,
    onGalleryToggle,
    showGenerator = true
}) => {
    // Sidebar state - read from localStorage and listen for changes
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('ezart_sidebar_open') !== 'false';
        }
        return true;
    });

    // Listen for sidebar state changes
    useEffect(() => {
        const updateSidebarState = () => {
            const leftOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
            setSidebarOpen(leftOpen);
        };

        // Listen for changes from other tabs
        window.addEventListener('storage', updateSidebarState);

        // Poll for changes from same tab (localStorage doesn't trigger storage event in same tab)
        const interval = setInterval(updateSidebarState, 200);

        return () => {
            window.removeEventListener('storage', updateSidebarState);
            clearInterval(interval);
        };
    }, []);

    // Calculate left position based on sidebar state
    const SIDEBAR_WIDTH = 240;
    const leftPosition = sidebarOpen ? SIDEBAR_WIDTH + 24 : 24;

    return (
        <div
            className="fixed bottom-6 z-[100] flex flex-col gap-2 p-2 bg-zinc-900/80 backdrop-blur-md border border-white/10 rounded-2xl shadow-xl transition-all duration-300"
            style={{ left: leftPosition }}
        >
            {/* Gallery Toggle */}
            <button
                onClick={onGalleryToggle}
                className={`p-3 rounded-xl transition-all ${
                    isGalleryOpen
                        ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 scale-105'
                        : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                title="Toggle Gallery (G)"
            >
                <GridFourIcon className="w-5 h-5" weight={isGalleryOpen ? 'fill' : 'regular'} />
            </button>

            {showGenerator && (
                <>
                    <div className="h-px w-8 bg-white/10 mx-auto my-1" />

                    {/* Generator Toggle */}
                    <button
                        onClick={onGeneratorToggle}
                        className={`p-3 rounded-xl transition-all ${
                            isGeneratorOpen
                                ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/20 scale-105'
                                : 'text-zinc-400 hover:text-white hover:bg-white/10'
                        }`}
                        title="Toggle Generator (Space)"
                    >
                        <SparkleIcon className="w-5 h-5" weight={isGeneratorOpen ? 'fill' : 'duotone'} />
                    </button>
                </>
            )}
        </div>
    );
};
